"use client";

import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { db } from "../config/firebase";
import { collection, getDocs } from "firebase/firestore";
import { useAuth } from "../AuthContext";
import { PAYPAL_CONFIG, validatePayPalConfig } from "../config/paypalConfig";
import { createSuitePayPalOrder, captureSuitePayPalOrder } from "../services/paypalService";
import { useCart } from "../context/CartContext";
import "../css/ReservationForm.css";
import "../css/Cart.css";

const parsePrice = (v) => {
  const n = parseFloat(String(v ?? 0).replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

// Your official 12 Suites + Villas
const villaSuiteNames = [
  "Sunset Palms Suite",
  "Azure Coast Suite",
  "Coral Reef Suite",
  "Luna Mar Suite",
  "Caribbean Breeze Villa",
  "Ocean Breeze Villa",
  "Blue Horizon Villa",
  "Tropical Hideaway Villa",
  "Vista Del Mar Villa",
  "Serenity Sands Villa",
  "Palm Grove Villa",
  "Emerald Bay Villa",
];

const nightsBetween = (checkIn, checkOut) => {
  const inDate = new Date(checkIn);
  const outDate = new Date(checkOut);
  const nights = Math.round((outDate - inDate) / (1000 * 60 * 60 * 24));
  return Number.isFinite(nights) ? nights : 0;
};

export default function ReservationForm() {
  const { currentUser, authReady } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    primaryGuest: "",
    secondaryGuest: "",
    tertiaryGuest: "",
    agePrimary: "",
    ageCompanion: "",
    ageTertiary: "",
    date: "",
    email: "",
    phone: "",
    checkIn: "",
    checkOut: "",
    note: "",
    villa: "",
  });

  const { addItem, items: cartItems } = useCart();

  const [availableVillas, setAvailableVillas] = useState([]);
  const [step, setStep] = useState("form"); // "form" | "payment"
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null);
  const [cartAdded, setCartAdded] = useState(false);
  const paymentStarted = useRef(false);

  useEffect(() => {
    const fetchAvailableVillas = async () => {
      try {
        const snap = await getDocs(collection(db, "villas"));
        const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        // Filter to official 12 names that are not explicitly marked unavailable
        const filtered = all.filter(
          (v) => villaSuiteNames.includes(v.name) && v.available !== false
        );
        setAvailableVillas(filtered);
      } catch (error) {
        console.error("Error fetching villas:", error);
      }
    };

    fetchAvailableVillas();
  }, []);

  // Pre-fill form when navigated from Cart's "Book This Item" button
  useEffect(() => {
    const cartItem = location.state?.cartItem;
    if (!cartItem || availableVillas.length === 0) return;
    setFormData((prev) => ({
      ...prev,
      primaryGuest: cartItem.primaryGuest || prev.primaryGuest,
      secondaryGuest: cartItem.guestName2 || prev.secondaryGuest,
      email: cartItem.email || prev.email,
      phone: cartItem.phone || prev.phone,
      checkIn: cartItem.checkIn || prev.checkIn,
      checkOut: cartItem.checkOut || prev.checkOut,
      note: cartItem.note || prev.note,
      villa: cartItem.itemName || prev.villa,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.cartItem, availableVillas.length]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const selectedVilla = availableVillas.find((v) => v.name === formData.villa);
  const nights = nightsBetween(formData.checkIn, formData.checkOut);

  const resetForm = () => {
    paymentStarted.current = false;
    setFormData({
      primaryGuest: "",
      secondaryGuest: "",
      tertiaryGuest: "",
      agePrimary: "",
      ageCompanion: "",
      ageTertiary: "",
      date: "",
      email: "",
      phone: "",
      checkIn: "",
      checkOut: "",
      note: "",
      villa: "",
    });
    setStep("form");
  };

  const handleAddToCart = () => {
    setSubmitMessage(null);
    if (!selectedVilla) {
      setSubmitMessage({ type: "error", text: "Please select a villa or suite." });
      return;
    }
    if (nights < 1) {
      setSubmitMessage({ type: "error", text: "Check-out date must be after check-in date." });
      return;
    }
    if (!formData.primaryGuest.trim()) {
      setSubmitMessage({ type: "error", text: "Please enter the primary guest name." });
      return;
    }
    if (!formData.email.trim()) {
      setSubmitMessage({ type: "error", text: "Please enter your email address." });
      return;
    }

    const category =
      selectedVilla.propertyType?.toLowerCase() === "villa" ? "villa" : "suite";
    const cartItemId = `${category}-${selectedVilla.id}-${formData.checkIn}`;

    if (cartItems.some((i) => i.cartItemId === cartItemId)) {
      setSubmitMessage({ type: "info", text: "This listing for those dates is already in your cart." });
      return;
    }

    addItem({
      cartItemId,
      category,
      itemId: selectedVilla.id,
      itemName: selectedVilla.name,
      price: selectedVilla.price || "",
      priceNumeric: parsePrice(selectedVilla.price),
      checkIn: formData.checkIn,
      checkOut: formData.checkOut,
      nights,
      date: formData.checkIn,
      guests: null,
      primaryGuest: formData.primaryGuest,
      guestName2: formData.secondaryGuest,
      email: formData.email,
      phone: formData.phone,
      note: formData.note,
      addedAt: Date.now(),
    });

    setCartAdded(true);
    setSubmitMessage({ type: "success", text: "Added to your cart!", link: { label: "View Cart →", to: "/cart" } });
    setTimeout(() => { setCartAdded(false); setSubmitMessage(null); }, 5000);
  };

  // Move from the details form to the payment step — no Firestore write happens
  // here. The booking is only created server-side, after PayPal confirms payment.
  const handleContinueToPayment = (e) => {
    e.preventDefault();
    setSubmitMessage(null);

    if (!selectedVilla) {
      setSubmitMessage({ type: "error", text: "Please select a villa or suite." });
      return;
    }
    if (nights < 1) {
      setSubmitMessage({ type: "error", text: "Check-out date must be after check-in date." });
      return;
    }

    setStep("payment");
  };

  if (!authReady) {
    return null;
  }

  if (!currentUser) {
    return (
      <div className="reservation-form-container">
        <h2>Suites &amp; Villas Reservation</h2>
        <p style={{ textAlign: "center", color: "white", position: "relative", zIndex: 2 }}>
          You'll need an account to book a suite or villa.{" "}
          <Link to="/sign-in" style={{ color: "white", textDecoration: "underline", fontWeight: 700 }}>
            Sign in
          </Link>{" "}
          or{" "}
          <Link to="/sign-up" style={{ color: "white", textDecoration: "underline", fontWeight: 700 }}>
            create an account
          </Link>{" "}
          to continue.
        </p>
      </div>
    );
  }

  return (
    <div className="reservation-form-container">
      <h2>Suites &amp; Villas Reservation</h2>

      {step === "form" && (
        <form className="reservation-form" onSubmit={handleContinueToPayment}>
          {/* 1. Guest names */}
          <div className="guest-names-row">
            <div className="form-group">
              <label htmlFor="primaryGuest">Primary Guest Name</label>
              <input
                type="text"
                id="primaryGuest"
                name="primaryGuest"
                placeholder="Primary guest name"
                value={formData.primaryGuest}
                onChange={handleChange}
                required
                autoComplete="off"
              />
            </div>

            <div className="form-group">
              <label htmlFor="secondaryGuest">Companion Guest Name</label>
              <input
                type="text"
                id="secondaryGuest"
                name="secondaryGuest"
                placeholder="Companion guest name"
                value={formData.secondaryGuest}
                onChange={handleChange}
                autoComplete="off"
              />
            </div>

            <div className="form-group">
              <label htmlFor="tertiaryGuest">Third Guest Name</label>
              <input
                type="text"
                id="tertiaryGuest"
                name="tertiaryGuest"
                placeholder="Third guest name"
                value={formData.tertiaryGuest}
                onChange={handleChange}
                autoComplete="off"
              />
            </div>
          </div>

          {/* 2. Ages */}
          <div className="age-row">
            <div className="form-group">
              <label htmlFor="agePrimary">Age – Primary Guest</label>
              <input
                id="agePrimary"
                name="agePrimary"
                type="number"
                placeholder="Enter age"
                value={formData.agePrimary}
                onChange={handleChange}
                min={1}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="ageCompanion">Age – Companion Guest</label>
              <input
                id="ageCompanion"
                name="ageCompanion"
                type="number"
                placeholder="Enter age"
                value={formData.ageCompanion}
                onChange={handleChange}
                min={1}
              />
            </div>

            <div className="form-group">
              <label htmlFor="ageTertiary">Age – Third Guest</label>
              <input
                id="ageTertiary"
                name="ageTertiary"
                type="number"
                placeholder="Enter age"
                value={formData.ageTertiary}
                onChange={handleChange}
                min={1}
              />
            </div>
          </div>

          {/* 3. Date */}
          <label htmlFor="date">Date</label>
          <input
            id="date"
            name="date"
            type="date"
            value={formData.date}
            onChange={handleChange}
            required
          />

          {/* 4. Email */}
          <label htmlFor="email">Email</label>
          <input
            id="email"
            name="email"
            type="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleChange}
            required
          />

          {/* 5. Phone */}
          <label htmlFor="phone">Phone Number</label>
          <input
            id="phone"
            name="phone"
            type="tel"
            placeholder="Phone Number"
            value={formData.phone}
            onChange={handleChange}
            required
          />

          {/* 6. Check-in / Check-out */}
          <div className="reservation-form-top-row">
            <div className="form-group">
              <label htmlFor="checkIn">Check-in</label>
              <input
                type="date"
                id="checkIn"
                name="checkIn"
                value={formData.checkIn}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="checkOut">Check-out</label>
              <input
                type="date"
                id="checkOut"
                name="checkOut"
                value={formData.checkOut}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* 7. Suites/Villas */}
          <label htmlFor="villa">Select Suite and/or Villa</label>
          <select
            id="villa"
            name="villa"
            value={formData.villa}
            onChange={handleChange}
            required
          >
            <option value="" disabled>
              Select an option
            </option>

            {villaSuiteNames.map((name) => {
              const villa = availableVillas.find((v) => v.name === name);
              return (
                <option key={name} value={name} disabled={!villa}>
                  {name}{villa?.price ? ` — ${villa.price}/night` : ""} {villa ? "" : "(Unavailable)"}
                </option>
              );
            })}
          </select>

          {/* 8. Special requests */}
          <label htmlFor="note">Special Requests</label>
          <textarea
            id="note"
            name="note"
            placeholder="Add any special requests (optional)"
            value={formData.note}
            onChange={handleChange}
            rows={3}
          />

          <button type="submit">Continue to Payment</button>
          <button
            type="button"
            className={`btn-add-to-cart${cartAdded ? " added" : ""}`}
            onClick={handleAddToCart}
          >
            {cartAdded ? "Added to Cart ✓" : "Add to Cart"}
          </button>
        </form>
      )}

      {step === "payment" && selectedVilla && (
        <div className="reservation-form" style={{ position: "relative", zIndex: 2 }}>
          <div
            style={{
              background: "rgba(255,255,255,0.1)",
              borderRadius: 12,
              padding: 16,
              marginBottom: 8,
            }}
          >
            <p style={{ margin: 0, fontWeight: 700 }}>{selectedVilla.name}</p>
            <p style={{ margin: 0 }}>
              {formData.checkIn} → {formData.checkOut} ({nights} night{nights === 1 ? "" : "s"})
            </p>
            <p style={{ margin: 0, opacity: 0.85, fontSize: "0.9rem" }}>
              Final total is calculated securely at checkout.
            </p>
          </div>

          {!validatePayPalConfig() ? (
            <p style={{ textAlign: "center", color: "#ffa1a1" }}>
              PayPal isn't configured yet. Add REACT_APP_PAYPAL_CLIENT_ID to your .env file.
            </p>
          ) : (
            <PayPalScriptProvider options={{ clientId: PAYPAL_CONFIG.clientId, currency: "USD" }}>
              <PayPalButtons
                style={{ layout: "vertical" }}
                disabled={isSubmitting}
                createOrder={async () => {
                  paymentStarted.current = true;
                  try {
                    const order = await createSuitePayPalOrder({
                      villaId: selectedVilla.id,
                      checkIn: formData.checkIn,
                      checkOut: formData.checkOut,
                    });
                    return order.orderId;
                  } catch (error) {
                    console.error("createOrder error:", error);
                    setSubmitMessage({
                      type: "error",
                      text: error.message || "Could not start checkout. Please try again.",
                    });
                    throw error;
                  }
                }}
                onApprove={async (data) => {
                  setIsSubmitting(true);
                  setSubmitMessage(null);
                  try {
                    const result = await captureSuitePayPalOrder(data.orderID, {
                      ...formData,
                      villaId: selectedVilla.id,
                      villaName: selectedVilla.name,
                      nights,
                    });
                    navigate("/booking-confirmed", {
                      state: {
                        pointsAwarded: result.pointsAwarded,
                        totalCharged: result.amountPaid,
                        bookingCount: 1,
                        newBalance: result.newBalance ?? null,
                      },
                    });
                  } catch (error) {
                    console.error("capture error:", error);
                    setSubmitMessage({
                      type: "error",
                      text: error.message || "Payment could not be completed. Please try again.",
                    });
                  } finally {
                    setIsSubmitting(false);
                  }
                }}
                onCancel={() => {
                  if (paymentStarted.current) {
                    setSubmitMessage({ type: "info", text: "Payment canceled." });
                    paymentStarted.current = false;
                  }
                }}
                onError={(error) => {
                  console.error("PayPal error:", error);
                  setSubmitMessage({
                    type: "error",
                    text: "Something went wrong with PayPal. Please try again.",
                  });
                }}
              />
            </PayPalScriptProvider>
          )}

          <button type="button" onClick={() => { paymentStarted.current = false; setStep("form"); }} disabled={isSubmitting}>
            Back to Details
          </button>
        </div>
      )}

      {submitMessage && (
        <div
          style={{
            marginTop: 12,
            position: "relative",
            zIndex: 2,
            color:
              submitMessage.type === "success"
                ? "#3eea94"
                : submitMessage.type === "info"
                ? "#ffd27a"
                : "#ffa1a1",
            textAlign: "center",
            fontWeight: 600,
            textShadow: "0 1px 10px rgba(0, 0, 0, 0.15)",
          }}
        >
          {submitMessage.text}
          {submitMessage.link && (
            <Link
              to={submitMessage.link.to}
              style={{ marginLeft: 8, color: "#d4a853", textDecoration: "underline" }}
            >
              {submitMessage.link.label}
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
